import { Box, Tabs } from '@mantine/core';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';

export interface SectionTab {
  id: string;
  label: string;
  icon: React.ReactNode;
}

export interface FormEditSectionTabsProps {
  sections: SectionTab[];
}

// Sticky tab strip + scrollspy. Clicking a tab smooth-scrolls to the matching
// <section id="...">. Scrolling updates the active tab to whichever section
// occupies the viewport's middle band (so we don't flicker on short sections
// that barely peek into view).
export function FormEditSectionTabs({ sections }: FormEditSectionTabsProps) {
  const [activeId, setActiveId] = useState<string>(sections[0]?.id ?? '');
  // Distinguish click-driven smooth scrolls from natural scrolls so we don't
  // override the click target mid-flight as sections cross the threshold.
  const userClickedRef = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (userClickedRef.current) return;
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          setActiveId(visible[0].target.id);
        }
      },
      // Active section = the one that owns the middle band of the viewport.
      { rootMargin: '-40% 0px -55% 0px', threshold: 0 }
    );

    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [sections]);

  const handleTabChange = (value: string | null) => {
    if (!value) return;
    setActiveId(value);
    userClickedRef.current = true;
    document.getElementById(value)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // Release the click guard once smooth scroll has settled.
    window.setTimeout(() => {
      userClickedRef.current = false;
    }, 700);
  };

  return (
    <Box
      style={{
        position: 'sticky',
        top: 60, // matches AppShell.Header height in Account layout
        zIndex: 100,
        backgroundColor: 'var(--mantine-color-body)',
        borderBottom: '1px solid var(--mantine-color-default-border)',
        marginBottom: 'var(--mantine-spacing-md)',
        // Allow the tab row to scroll horizontally on narrow viewports.
        overflowX: 'auto',
      }}
    >
      <Tabs
        value={activeId}
        onChange={handleTabChange}
        variant="default"
        styles={{
          list: {
            flexWrap: 'nowrap',
            borderBottom: 'none',
          },
          tab: {
            borderBottomWidth: 2,
            whiteSpace: 'nowrap',
          },
        }}
      >
        <Tabs.List>
          {sections.map((s) => (
            <Tabs.Tab key={s.id} value={s.id} leftSection={s.icon}>
              {s.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>
      </Tabs>
    </Box>
  );
}
